import { effect, inject, Injectable, signal, untracked } from '@angular/core';
import { ApiService } from './api.service';
import { Car } from '../models/car-model';
import { RealEstate } from '../models/real-estate.model';
import { MapListing } from '../models/map-listing.model';

type Card = Car | RealEstate;
type Category = 'car' | 'home';
const sameIds = (a: readonly number[], b: readonly number[]) =>
  a.length === b.length && a.every((id, index) => id === b[index]);

/** Scoped to one result component; shared by its map and the cards below it. */
@Injectable()
export class MapListingCards {
  private readonly api = inject(ApiService);
  private readonly category = signal<Category>('car');
  private readonly active = signal(true);
  private readonly visibleIds = signal<readonly number[]>([], { equal: sameIds });
  private readonly selectedId = signal<number | null>(null);
  private readonly data = signal<Record<number, Card | null>>({});
  private readonly retryVersion = signal(0);
  private source?: readonly MapListing[];
  readonly loading = signal(false);
  readonly error = signal(false);

  private readonly fetchCards = effect(onCleanup => {
    if (!this.active()) { this.loading.set(false); return; }
    this.retryVersion();
    const category = this.category();
    const cache = this.data();
    const selected = this.selectedId();
    const ids = [...new Set([...(selected == null ? [] : [selected]), ...this.visibleIds()])]
      .filter(id => !(id in cache)).slice(0, 48);
    if (this.error() || !ids.length) { this.loading.set(false); return; }
    this.loading.set(true);
    const subscription = this.api.getMapCards(category, ids).subscribe({
      next: cards => {
        // Do not overwrite a newer favorite response received while this batch was in flight.
        this.data.update(current => {
          const next = { ...current };
          for (const id of ids) if (!(id in next)) next[id] = cards.find(card => card.id === id) ?? null;
          return next;
        });
        this.loading.set(false);
        if (cards.length < ids.length) this.error.set(true);
      },
      error: () => { this.loading.set(false); this.error.set(true); },
    });
    // A new search, changed selection, navigation, or destruction aborts obsolete HTTP work.
    onCleanup(() => subscription.unsubscribe());
  });

  bind(category: Category, items: readonly MapListing[] | undefined, visibleIds: number[], active = true): void {
    this.active.set(active);
    this.category.set(category);
    const full = (items ?? []).filter((item): item is Car | RealEstate => 'pictures' in item);
    // Also detect a fresh response if Angular coalesced the intermediate loading state.
    const freshMarkers = items !== undefined && items !== this.source && full.length === 0;
    if ((items === undefined && this.source !== undefined) || freshMarkers) {
      this.data.set({});
      if (items === undefined) this.selectedId.set(null);
      this.error.set(false);
    }
    this.source = items;
    this.visibleIds.set(items ? visibleIds : []);
    // Existing reducers replace a marker with the full response after a favorite/update action.
    if (full.length) this.data.update(current => ({ ...current, ...Object.fromEntries(full.map(card => [card.id, card])) }));
  }

  select(id: number | null): void {
    if (untracked(this.selectedId) === id) return;
    this.error.set(false);
    this.selectedId.set(id);
  }

  card(id: number): Card | null { return this.data()[id] ?? null; }
  cars(ids: number[]): Car[] { return ids.map(id => this.card(id)).filter((card): card is Car => card != null) as Car[]; }
  properties(ids: number[]): RealEstate[] { return ids.map(id => this.card(id)).filter((card): card is RealEstate => card != null) as RealEstate[]; }
  retry(): void {
    this.data.update(current => Object.fromEntries(Object.entries(current).filter(([, card]) => card != null)));
    this.error.set(false);
    this.retryVersion.update(value => value + 1);
  }
}
